import API from '@app/api';
import Config from '@app/config';
import Errors from '@app/action/errors';
import Notification from '@app/action/notification';
import Pending from '@app/action/pending';

import ActionHelper from '@module/redux-actions';
import Analytics from '@module/analytics';
import Router from '@module/router';
import { HttpResponseCodes } from '@module/http/axios/response';

import FormDataHelper from './helper/formdata';

export const actions = ActionHelper.types([
  'GET_ACCOUNT',
  'GET_SUBSCRIPTION',
  'GET_LAST_ORDER',
  'GET_CHECKOUT_DETAILS',

  'REMOVE_TEAM_MEMBER_SUCCESS',
  'INVITE_TO_TEAM_SUCCESS',
  'UPDATE_TEAM_MEMBER_SUCCESS',

  'REFRESH_ACCOUNT',

  'ACCOUNT_UPDATE_SUCCESS',
  'CANCEL_SUBSCRIPTION_SUCCESS',
  'REACTIVATE_SUBSCRIPTION_SUCCESS',

  'RESET_ACCOUNT_STATE',
  'PAYMENT_SUCCESS',
]);

export default ActionHelper.generate({
  getAccount(accountId) {
    const pendingId = 'getAccount';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getAccount(null, {
          pathExtValues: {
            accountId,
          },
        });

        dispatch({
          type: actions.GET_ACCOUNT,
          details: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_account_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  createSubdomain(accountId, params = {}, options = {}) {
    const pendingId = 'editAccount';
    const showNotification = !!options.showNotification;

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.createSubdomain(params, {
          pathExtValues: {
            accountId,
          },
        });

        dispatch({
          type: actions.ACCOUNT_UPDATE_SUCCESS,
          details: response.body,
        });

        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'notification',
              'update_profile_success'
            )
          );
        }
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification('error', 'update_profile_error')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  editAccount(accountId, params = {}, options = {}) {
    const pendingId = 'editAccount';
    const showNotification =
      options.notification === undefined || options.notification === true
        ? true
        : false;

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.editAccount(params, {
          pathExtValues: {
            accountId: accountId,
          },
        });

        dispatch({
          type: actions.ACCOUNT_UPDATE_SUCCESS,
          details: response.body,
        });

        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'notification',
              'update_profile_success'
            )
          );
        }
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification('error', 'update_profile_error')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getSubscription(accountId = null, subscriptionId = null, options = {}) {
    const pendingId = 'getSubscription';
    const showNotification = Boolean(options.showNotification);

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      let params;
      if (accountId) {
        params = { account_id: accountId };
      }

      try {
        const response = await API.Account.getSubscriptionDetails(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          details: response.body,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.UNAUTHORIZED) {
          // Team member
          dispatch({
            type: actions.GET_SUBSCRIPTION,
            details: {
              active: true,
              billing: 'monthly',
            },
          });
        } else if (showNotification) {
          dispatch(
            Notification.setNotification(
              'error',
              'retrieve_subscription_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updateSubscription(params = {}) {
    const pendingId = 'updateSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.updateSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          details: response.body,
        });

        dispatch(
          Notification.setNotification('notification', 'update_subscription')
        );
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(
            Notification.setNotification('error', 'autorenewal_not_supported')
          );
        } else {
          dispatch(
            Notification.setNotification(
              'error',
              'update_subscription_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  renewSubscription(params = {}, subscriptionId = null) {
    const pendingId = 'renewSubscription';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      // Remove vat if it only has the prefix
      if (params.vat_number && params.vat_number.length <= 2) {
        params.vat_number = '';
      }

      try {
        const response = await API.Account.renewSubscription(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        if (response.body.order_completed && response.body.redirect_to_url) {
          const url = new URL(response.body.redirect_to_url);
          location.assign(url.href);
        }

        const { template } = response.body.payment_step;
        FormDataHelper.submit(template.formdata, template.form);
        setTimeout(
          () => dispatch(Pending.resetPending(pendingId)),
          Config.actions.extended_pending
        );
      } catch (error) {
        dispatch(Errors.setError('account', error.body.fields));
        dispatch(
          Notification.setNotification('error', 'renew_subscription_exception')
        );
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  cancelSubscription(params = {}) {
    const pendingId = 'cancelSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.cancelSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.CANCEL_SUBSCRIPTION_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'cancel_subscription_success'
          )
        );
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'cancel_subscription_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  reactivateSubscription(params = {}) {
    const pendingId = 'reactivateSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.reactivateSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.REACTIVATE_SUBSCRIPTION_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'reactivate_subscription_success'
          )
        );
      } catch (e) {
        dispatch(
          Notification.setNotification(
            'error',
            'reactivate_subscription_exception'
          )
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  duplicateSubscription(params = {}, subscriptionId = null) {
    const pendingId = 'duplicateSubscription';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.duplicateSubscription(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          active: false,
          details: response.body,
        });

        Router.navigateTo('/payment/methods', false, {
          search: 'renewal=true',
        });
      } catch (e) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getLastOrder(subscriptionId = null) {
    const pendingId = 'getLastOrder';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getLastOrder(null, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_LAST_ORDER,
          details: response.body,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.NOT_FOUND) {
          Router.navigateTo('/account/renew-billing');
        } else {
          dispatch(
            Notification.setNotification(
              'error',
              'retrieve_last_order_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  completeLastOrder(subscriptionId = null) {
    const pendingId = 'completeLastOrder';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.completeLastOrder(null, {
          pathExtValues: {
            subscriptionId,
          },
        });

        const { user, account } = getState();
        Analytics.pushDataLayerEvent({
          category: 'Plus',
          action: 'Payment Failed Retry',
          user: {
            user_id: user.loggedIn,
            has_transacted: false,
            subscription_type: account.lastOrder.duration,
          },
          value: account.lastOrder.total_amount,
        });

        // Get our formdata template and submit it
        const { template } = response.body.payment_step;
        // We're being redirected, so keep the pending state a bit longer.
        FormDataHelper.submit(template.formdata, template.form);
        setTimeout(
          () => dispatch(Pending.resetPending(pendingId)),
          Config.actions.extended_pending
        );
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'complete_last_order_exception')
        );
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  removeMember(membership) {
    const pendingId = 'removeMember';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.Account.removeTeamMember(null, {
          pathExtValues: {
            membershipId: membership.id,
          },
        });

        dispatch({
          type: actions.REMOVE_TEAM_MEMBER_SUCCESS,
          membership: membership,
        });

        dispatch(
          Notification.setNotification('notification', 'removed_team_member', {
            fullName: membership.user.full_name,
          })
        );
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'remove_team_member_error')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  changeRole(membership, newRole) {
    const pendingId = 'updateMember';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const newMember = { ...membership, role: newRole };
        const response = await API.Account.updateTeamMember(newMember, {
          pathExtValues: {
            membershipId: newMember.id,
          },
        });

        dispatch({
          type: actions.UPDATE_TEAM_MEMBER_SUCCESS,
          membership: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'changed_team_member_role',
            { fullName: membership.user.full_name }
          )
        );
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'change_team_member_role_error')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getCheckoutDetails(subscriptionId = null) {
    const pendingId = 'getCheckoutDetails';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getCheckoutDetails(null, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_CHECKOUT_DETAILS,
          details: response.body.checkout,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_checkout_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetAccountState: [actions.RESET_ACCOUNT_STATE],
});
